import { CopropertyDto } from "api/coproperty"
import { OCCUPANCY_OPTIONS, buildingStandingLabel } from "features/subscription-process/coproperty/CopropertyFieldControls"
import {
  ClaimsSection,
  ComplementsSection,
  SummarySection,
} from "features/subscription-process/pricing-process/components/insured-entity-card/CopropertySummaryPanel"
import { Building, Check, MapPin } from "lucide-react"
import { FC, ReactNode } from "react"

/** Surface en m² au format français ("2 500 m²"). */
const formatArea = (value: number): string => `${Math.round(value).toLocaleString("fr-FR")} m²`

/** Montant en euros sans décimales ("1 976 000 €"). */
const formatAmount = (value: number): string =>
  value.toLocaleString("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 })

const yesNo = (value: boolean | null | undefined): string => (value == null ? "—" : value ? "Oui" : "Non")

/** Libellé des natures cochées ("Habitation, commerces"). */
const naturesLabel = (coproperty: CopropertyDto): string => {
  const natures = [
    coproperty.natureHousing && "Habitation",
    coproperty.natureOffices && "Bureaux",
    coproperty.natureShops && "Commerces",
    coproperty.natureParking && "Parking",
  ].filter((n): n is string => Boolean(n))
  if (natures.length === 0) return "—"
  return natures.map((n, index) => (index === 0 ? n : n.toLowerCase())).join(", ")
}

const Tile: FC<{ label: string; value: string; highlight?: boolean }> = ({ label, value, highlight }) => (
  <div className="flex flex-col items-center justify-center gap-1 px-4 py-4 text-center">
    <span className={`text-2xl font-bold ${highlight ? "text-primary" : "text-gray-800"}`}>{value}</span>
    <span className="text-sm text-gray-600">{label}</span>
  </div>
)

const Row: FC<{ label: string; value: ReactNode }> = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-4 py-1.5">
    <dt className="text-sm text-gray-500">{label}</dt>
    <dd className="text-right text-sm font-medium text-gray-900">{value}</dd>
  </div>
)

interface CopropertyOverviewBodyProps {
  coproperty: CopropertyDto
  /** Action of the risk questionnaire block; the block is shown only when this prop is given. */
  questionnaireAction?: ReactNode
}

/**
 * Read-only description of a coproperty: identity banner, key figures, the coproperty as
 * described (land, natures, buildings) and the declarations (questionnaire, current
 * contract, claims). Shared by the MRI contract tab and the validation page.
 */
export const CopropertyOverviewBody: FC<CopropertyOverviewBodyProps> = ({ coproperty, questionnaireAction }) => {
  const address = coproperty.address?.adresseFormattee || coproperty.address?.ligne1 || null
  const buildings = coproperty.buildings ?? []
  const buildingsAreaSum = buildings.reduce((sum, building) => sum + (building.area ?? 0), 0)

  return (
    <div className="flex flex-col gap-4">
      {/* Bandeau d'identité et chiffres clés */}
      <div className="overflow-hidden rounded-xl border bg-white">
        <div className="flex items-center gap-4 bg-primary/80 px-6 py-5 text-white">
          <span className="flex h-14 w-14 flex-none items-center justify-center rounded-xl bg-white/20">
            <Building className="h-7 w-7" />
          </span>
          <div className="min-w-0">
            <h2 className="truncate text-2xl font-bold">{coproperty.name || address || "Copropriété"}</h2>
            {address && (
              <p className="mt-1 flex items-center gap-1 text-sm opacity-90">
                <MapPin size={16} />
                {address}
              </p>
            )}
            {coproperty.registrationNumber && (
              <p className="text-sm opacity-90">Immatriculation {coproperty.registrationNumber}</p>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 divide-x divide-gray-100 md:grid-cols-4">
          <Tile label="Surface retenue" value={coproperty.retainedArea != null ? formatArea(coproperty.retainedArea) : "—"} />
          <Tile label="Sinistre maximum possible" value={coproperty.smp != null ? formatAmount(coproperty.smp) : "—"} highlight />
          <Tile label="Standing" value={buildingStandingLabel(coproperty.buildingStanding) ?? "—"} />
          <Tile label={buildings.length > 1 ? "Bâtiments" : "Bâtiment"} value={String(buildings.length)} />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Colonne gauche : la copropriété telle que décrite */}
        <div className="flex flex-col gap-4">
          <SummarySection title="Surfaces et terrain">
            <dl className="divide-y divide-gray-100">
              <Row label="Surface déclarée" value={coproperty.area != null ? formatArea(coproperty.area) : "—"} />
              <Row label="Σ surfaces bâtiments" value={buildingsAreaSum > 0 ? formatArea(buildingsAreaSum) : "—"} />
              <Row label="Espace vert" value={coproperty.greenSpaceArea != null ? formatArea(coproperty.greenSpaceArea) : "—"} />
              <Row
                label="Altitude"
                value={[
                  coproperty.altitudeMeters != null && `${Math.round(coproperty.altitudeMeters)} m`,
                  coproperty.above1500mAltitude && "(plus de 1 500 m)",
                ]
                  .filter(Boolean)
                  .join(" ") || "—"}
              />
              <Row label="Poste de secours à moins de 3 km" value={yesNo(coproperty.fireStationWithin3Km)} />
              <Row label="Construite depuis plus de 10 ans" value={yesNo(coproperty.builtMoreThan10YearsAgo)} />
              <Row label="Classée ou répertoriée" value={yesNo(coproperty.listedOrRegistered)} />
            </dl>
            <p className="mt-3 text-xs text-gray-400">
              La surface retenue est le maximum entre la surface déclarée et la somme des surfaces des
              bâtiments. Le SMP vaut la surface retenue × la valeur de reconstruction du standing de l'immeuble.
            </p>
          </SummarySection>

          <SummarySection title="Natures">
            <dl className="divide-y divide-gray-100">
              <Row label="Natures" value={naturesLabel(coproperty)} />
              {coproperty.natureShops && (
                <Row
                  label="Commerces"
                  value={[
                    coproperty.shopsArea != null && formatArea(coproperty.shopsArea),
                    coproperty.shopType,
                  ]
                    .filter(Boolean)
                    .join(" · ") || "—"}
                />
              )}
              {coproperty.natureOffices && (
                <Row label="Bureaux" value={coproperty.officesArea != null ? formatArea(coproperty.officesArea) : "—"} />
              )}
              {coproperty.natureParking && (
                <Row label="Parking commercial" value={coproperty.commercialParkingArea != null ? formatArea(coproperty.commercialParkingArea) : "—"} />
              )}
              <Row label="Occupée à moins de 50 %" value={yesNo(coproperty.occupiedLessThan50Percent)} />
            </dl>
          </SummarySection>

          <SummarySection title={`Bâtiments (${buildings.length})`}>
            {buildings.length === 0 ? (
              <p className="text-sm text-gray-500">Aucun bâtiment décrit.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                      <th className="pb-2 pr-3 font-semibold">Bâtiment</th>
                      <th className="pb-2 pr-3 text-right font-semibold">Surface</th>
                      <th className="pb-2 pr-3 font-semibold">Niveaux</th>
                      <th className="pb-2 pr-3 font-semibold">Sous-sols</th>
                      <th className="pb-2 font-semibold">Occupation</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {buildings.map((building, index) => (
                      <tr key={building.id ?? index}>
                        <td className="py-2 pr-3 font-medium text-gray-900">
                          <span className="flex items-center gap-2">
                            <Building className="h-4 w-4 flex-none text-primary" />
                            Bâtiment {index + 1}
                          </span>
                        </td>
                        <td className="py-2 pr-3 text-right text-gray-700">{building.area != null ? formatArea(building.area) : "—"}</td>
                        <td className="py-2 pr-3 text-gray-700">
                          {building.floorsCount == null ? "—" : building.floorsCount === 0 ? "Rez-de-chaussée" : `R+${building.floorsCount}`}
                        </td>
                        <td className="py-2 pr-3 text-gray-700">{building.basementsCount ?? "—"}</td>
                        <td className="py-2 text-gray-700">
                          {OCCUPANCY_OPTIONS.find((o) => o.value === building.occupancy)?.label ?? "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SummarySection>
        </div>

        {/* Colonne droite : les déclarations du dossier */}
        <div className="flex flex-col gap-4">
          {questionnaireAction !== undefined && (
            <SummarySection title="Questionnaire d'appréciation du risque" action={questionnaireAction}>
              <p className="flex items-center gap-2 text-sm text-gray-700">
                <Check className="h-4 w-4 text-green-700" />
                Aucune exclusion déclenchée.
              </p>
            </SummarySection>
          )}

          <ComplementsSection coproperty={coproperty} />

          <ClaimsSection coproperty={coproperty} />
        </div>
      </div>
    </div>
  )
}
